/**
 * Apply a resolution answer to BreakdownState.
 * Sets the field as user-confirmed with full confidence.
 */

import type { ExtractionFieldData } from '@/components/intake/ExtractionField';
import type { BreakdownState } from './buildBreakdownState';
import { computeBreakdownReadiness } from './computeBreakdownReadiness';

export function applyResolutionAnswer(
  state: BreakdownState,
  fieldName: string,
  value: string | number,
): BreakdownState {
  const normalizedValue = typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : value;

  const existingField = state.fields[fieldName];
  const updatedField = {
    ...(existingField || {
      confidence: 0,
      source_snippet: null,
      isCritical: true,
      isRequiredToProceed: true,
    }),
    fieldName,
    value: normalizedValue,
    source: 'user' as const,
    confidence: 1.0,
    detection_status: 'detected' as const,
    confirmation_status: 'confirmed' as const,
    isEditing: false,
    editDraft: null,
    hasValidationError: false,
    validationError: null,
    touched: true,
    updated_at: new Date().toISOString(),
  };

  const fields = { ...state.fields, [fieldName]: updatedField };
  const missingFields = state.missingFields.filter(f => f !== fieldName);

  const extractionFields: Record<string, ExtractionFieldData> = {};
  for (const [k, v] of Object.entries(fields)) {
    extractionFields[k] = v;
  }

  const readiness = computeBreakdownReadiness(extractionFields, missingFields, state.conflicts);

  return { ...state, fields, missingFields, readiness };
}
